import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components'

import { Typo, Textinput, SectionSeparation, SwitchRow } from '../../../../ui';
import { MenuItem, Select } from '@mui/material';
import { courseAccessTimePeriod, secondsToPeriod } from '../../courseUtils';
import { useSectionsActions } from '../../../sections/hooks/useSectionsActions';
import { useUpdateCourse } from '../../hooks';
import { useTranslation } from 'react-i18next';

const DripContentSection = styled.div`
  margin: 1rem 0 1.5rem 0;
`

const GatedAccessWrapper = styled.div`
  margin: 1rem 0;
`

const DripContentDelay = styled.div`
  display: flex;
  align-items: baseline;
`

const TextinputStyled = styled(Textinput)`
  margin-right: 0.5rem;
  max-width: 50px;
`

const SelectStyled = styled(Select)`
  margin-right: 0.5rem;
  min-width: 100px;
`

const DripContentWrapper = styled.div`
  max-width: 70%;
  min-width: 300px;
`

const Seconds = {
  hour: 3600,
  day: 86400,
  month: 2592000
}

const CourseDripContentEdit = ({ course, courseToc, }) => {
  const updateCourse = useUpdateCourse();
  const { t } = useTranslation();
  const { updateSection } = useSectionsActions();

  const [selectedTimePeriod, setSelectedTimePeriod] = useState({});
  const [gateAccess, setGateAccess] = useState(false);
  const [delayAccess, setDelayAccess] = useState(false);

  useEffect(() => {
    setDelayAccess(course.settings?.dripContent?.delayedAccess || false);
    setGateAccess(course.settings?.dripContent?.gatedAccess || false);

    if (courseToc && courseToc.length) {
      const tocData = {};
      courseToc.map((section) => {
        const period = secondsToPeriod(section.accessDelay)
        return tocData[section.id] = { type: period.type, value: period.amount }
      });
      setSelectedTimePeriod(tocData);
    }
  }, [course, courseToc]);

  const handleChangeAccess = useCallback(async (event, prop) => {
    const isChecked = event.target.checked;

    // When turning off delayedAccess, reset all section delays to zero
    if (prop === 'delayedAccess' && !isChecked && courseToc?.length) {
      await Promise.all(
        courseToc.map((section) =>
          updateSection({
            sectionData: {
              id: section.id,
              accessDelay: 0,
              courseId: course.id,
            }
          })
        )
      );
      // Reset local state
      const resetTimePeriod = {};
      courseToc.forEach((section) => {
        resetTimePeriod[section.id] = { type: 'hour', value: 0 };
      });
      setSelectedTimePeriod(resetTimePeriod);
    }

    const categories = course.categories?.map(cat => typeof cat === 'string' ? cat : cat.slug) || [];
    const courseData = {
      description:course.description,
      categories,
      id: course.id,
      settings: {
        ...course.settings,
        dripContent: {
          ...course.settings?.dripContent,
          [prop]: isChecked
        }
      }
    };

    updateCourse({ courseData });
  }, [updateCourse, updateSection, course, courseToc]);

  const handleDelayChange = useCallback(async (event, targetId, property) => {
    let accessDelay;
    setSelectedTimePeriod({ ...selectedTimePeriod, [targetId]: { ...selectedTimePeriod[targetId], [property]: event.target.value } });

    if (property === 'value') {
      accessDelay = event.target.value * Seconds[selectedTimePeriod[targetId].type];
    } else {
      accessDelay = selectedTimePeriod[targetId].value * Seconds[event.target.value];
    }

    const sectionData = {
      id: targetId,
      accessDelay,
      courseId: course.id,
    };

    await updateSection({ sectionData });
  }, [updateSection, selectedTimePeriod]);

  return (
    <DripContentWrapper>
      <Typo.ContentBigHeader $textAlign='left'>{t('course.dripContent')}</Typo.ContentBigHeader>
      <GatedAccessWrapper>
        <SwitchRow
          label={t('course.enableGateAccess')}
          checked={gateAccess}
          onChange={(e) => handleChangeAccess(e, 'gatedAccess')}
          enabledText={t('globals.enabled')}
          disabledText={t('globals.disabled')}
        />
        <Typo.Info>{t('course.gateAccessDescription')}</Typo.Info>
      </GatedAccessWrapper>
      <SectionSeparation />
      <GatedAccessWrapper>
        <SwitchRow
          label={t('course.delayedAccess')}
          checked={delayAccess}
          onChange={(e) => handleChangeAccess(e, 'delayedAccess')}
          enabledText={t('globals.enabled')}
          disabledText={t('globals.disabled')}
        />
        <Typo.Info>{t('course.delayedAccessDescription')}</Typo.Info>
        {delayAccess && courseToc?.map((section) =>
          <DripContentSection key={section.id}>
            <Typo.ContentSmallHeader $textAlign='left'>{section.name}</Typo.ContentSmallHeader>
            <Typo.Info >{t('course.makeItAvailableAfter')}</Typo.Info>
            <DripContentDelay>
              <TextinputStyled
                propName={'timeAmount'}
                defaultValue={(selectedTimePeriod && selectedTimePeriod[section.id]?.value) || '0'}
                onBlurValue={e => handleDelayChange(e, section.id, 'value')}
                variant='standard'
                type='number'
              />
              <SelectStyled
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                variant='standard'
                value={(selectedTimePeriod && selectedTimePeriod[section.id]?.type) || ''}
                onChange={(e) => handleDelayChange(e, section.id, 'type')}
              >
                <MenuItem value='' />
                {courseAccessTimePeriod.map((period) =>
                  <MenuItem key={period.value} value={period.value}>{period.text}</MenuItem>
                )}
              </SelectStyled>
            </DripContentDelay>
          </DripContentSection>
        )}
      </GatedAccessWrapper>
    </DripContentWrapper>

  );
};

export default CourseDripContentEdit;
